import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff, ImagePlus, Pencil, Trash2, Type } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { AdminNav } from "@/components/AdminNav";
import { SceneBreakSample } from "@/components/SceneBreakCard";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const card = "rounded-2xl border border-line bg-panel p-5 space-y-4";
const btn = "inline-flex items-center gap-1.5 rounded-full bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-onaccent text-sm font-semibold px-5 h-10";

const TypedForm = ({ onAdd }) => {
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { onAdd((await api.post("/admin/scene-breaks", { name, text })).data); setName(""); setText(""); toast.success("Design added"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={submit} className={card} data-testid="sb-typed-form">
      <p className="flex items-center gap-2 text-slate-200"><Type className="w-4 h-4 text-amber-400" /> Typed design</p>
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name, e.g. Moon and stars" maxLength={60} required data-testid="sb-typed-name" />
      <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Characters, e.g. ☾ ✦ ☽" maxLength={40} required className="font-display text-lg" data-testid="sb-typed-text" />
      <div className="rounded-xl bg-inset px-4"><SceneBreakSample d={{ id: "typed-preview", kind: "text", text: text || " " }} /></div>
      <button disabled={!name.trim() || !text.trim()} className={btn} data-testid="sb-typed-submit">Add design</button>
    </form>
  );
};

const ImageForm = ({ onAdd }) => {
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const url = useMemo(() => (file ? URL.createObjectURL(file) : ""), [file]);
  useEffect(() => () => url && URL.revokeObjectURL(url), [url]);
  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append("name", name);
    fd.append("file", file);
    setBusy(true);
    try { onAdd((await api.post("/admin/scene-breaks/image", fd)).data); setName(""); setFile(null); input.current.value = ""; toast.success("Design added"); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className={card} data-testid="sb-image-form">
      <p className="flex items-center gap-2 text-slate-200"><ImagePlus className="w-4 h-4 text-amber-400" /> Image design</p>
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name, e.g. Gilded flourish" maxLength={60} required data-testid="sb-image-name" />
      <input ref={input} type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={(e) => setFile(e.target.files[0] || null)}
        className="block w-full text-xs text-slate-400 file:mr-3 file:rounded-full file:border-0 file:bg-raised file:px-4 file:py-2 file:text-slate-200" data-testid="sb-image-file" />
      <div className="rounded-xl bg-inset px-4">{url ? <SceneBreakSample d={{ id: "image-preview", kind: "image", image_url: url }} /> : <p className="py-5 text-center text-xs text-slate-500">PNG, JPG, GIF or WebP, up to 2 MB. Wide, short images with a clear background look best.</p>}</div>
      <button disabled={!name.trim() || !file || busy} className={btn} data-testid="sb-image-submit">{busy ? "Uploading…" : "Upload design"}</button>
    </form>
  );
};

const Row = ({ d, onChange, onDelete }) => {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(d.name);
  const [text, setText] = useState(d.text);
  const save = async (body) => {
    try { onChange((await api.put(`/admin/scene-breaks/${d.id}`, body)).data); setEdit(false); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <li className={`flex flex-wrap items-center gap-4 py-3 ${d.active ? "" : "opacity-60"}`} data-testid={`sb-row-${d.id}`}>
      <div className="w-40 shrink-0 rounded-xl bg-inset px-3"><SceneBreakSample d={d} /></div>
      {edit ? (
        <div className="flex flex-1 flex-wrap gap-2 min-w-[14rem]">
          <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 flex-1" data-testid={`sb-edit-name-${d.id}`} />
          {d.kind === "text" && <Input value={text} onChange={(e) => setText(e.target.value)} className="h-9 flex-1" data-testid={`sb-edit-text-${d.id}`} />}
          <button onClick={() => save(d.kind === "text" ? { name, text } : { name })} className="text-xs text-amber-300" data-testid={`sb-edit-save-${d.id}`}>Save</button>
          <button onClick={() => setEdit(false)} className="text-xs text-slate-400">Cancel</button>
        </div>
      ) : (
        <div className="flex-1 min-w-[10rem]">
          <p className="text-sm text-slate-200">{d.name}</p>
          <p className="text-[11px] font-code text-slate-500">{d.builtin ? "Built in" : d.kind === "image" ? "Image" : "Typed"}{d.pro ? " · Pro" : " · Free default"}{d.active ? "" : " · Hidden"}</p>
        </div>
      )}
      {!d.builtin && !edit && (
        <div className="flex items-center gap-1">
          <button onClick={() => setEdit(true)} title="Edit" className="p-2 text-slate-400 hover:text-amber-300" data-testid={`sb-edit-${d.id}`}><Pencil className="w-4 h-4" /></button>
          <button onClick={() => save({ active: !d.active })} title={d.active ? "Hide from writers" : "Show to writers"} className="p-2 text-slate-400 hover:text-amber-300" data-testid={`sb-toggle-${d.id}`}>
            {d.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
          <button onClick={() => onDelete(d)} title="Delete" className="p-2 text-slate-400 hover:text-rose-400" data-testid={`sb-delete-${d.id}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      )}
    </li>
  );
};

export default function AdminSceneBreaks() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [del, setDel] = useState(null);
  const isAdmin = user?.role === "admin";
  useEffect(() => { if (isAdmin) api.get("/admin/scene-breaks").then((r) => setList(r.data)); }, [isAdmin]);
  if (!isAdmin) return <Navigate to="/worlds" replace />;
  const add = (d) => setList((l) => [...l, d]);
  const change = (d) => setList((l) => l.map((x) => (x.id === d.id ? d : x)));
  const remove = async () => {
    try { await api.delete(`/admin/scene-breaks/${del.id}`); setList((l) => l.filter((x) => x.id !== del.id)); toast.success("Design deleted"); }
    catch (err) { toast.error(formatApiError(err)); }
    setDel(null);
  };
  return (
    <div className="relative z-10 min-h-screen px-6 md:px-12 py-10 max-w-6xl mx-auto" data-testid="admin-scene-breaks-page">
      <Link to="/worlds" className="flex items-center gap-2 text-sm text-slate-400 hover:text-amber-300 mb-10 w-fit" data-testid="admin-back-link"><ArrowLeft className="w-4 h-4" /> Back to worlds</Link>
      <p className="font-code text-xs uppercase tracking-[0.3em] text-amber-500/80 mb-3">Admin</p>
      <h1 className="font-display text-4xl sm:text-5xl text-amber-50 mb-3">Scene breaks</h1>
      <p className="text-sm text-slate-400 mb-8">Designs added here appear for every Pro writer. Free writers always get the Asterism.</p>
      <AdminNav />
      <div className="grid md:grid-cols-2 gap-5"><TypedForm onAdd={add} /><ImageForm onAdd={add} /></div>
      <ul className="mt-10 rounded-2xl border border-line bg-panel px-5 divide-y divide-line" data-testid="sb-list">
        {list.map((d) => <Row key={d.id} d={d} onChange={change} onDelete={setDel} />)}
      </ul>
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="bg-panel border-line" data-testid="sb-delete-dialog">
          <AlertDialogTitle className="font-display text-2xl text-amber-50">Delete "{del?.name}"?</AlertDialogTitle>
          <AlertDialogDescription className="text-slate-400">Works using it go back to the writer's default. To keep it for existing works, hide it instead.</AlertDialogDescription>
          <AlertDialogFooter><AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-500 hover:bg-rose-400 text-white" data-testid="sb-delete-confirm">Delete design</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
